import { useAuthStore } from '@/store/authStore'
import { userHasPermission, type PermissionKey } from '@/utils/permissions'

/**
 * usePermissions.ts
 * Pattern from react_sample: named hook per permission check.
 * Returns hasPermission(key) — mirrors react_sample's usePermissions.ts.
 *
 * Usage (preferred — named hook pattern):
 *   export function useCanExportReport() {
 *     const { hasPermission } = usePermissions()
 *     return hasPermission('AttendanceTrackingBulkReportExport')
 *   }
 *
 * Usage (direct):
 *   const { hasPermission } = usePermissions()
 *   if (hasPermission('CapacityApplication')) { ... }
 */
export function usePermissions() {
  const roles = useAuthStore((s) => s.roles)

  return {
    hasPermission: (permission: PermissionKey): boolean =>
      userHasPermission(roles, permission),
    roles,
  }
}

// ── Named permission hooks (add more as features are implemented) ─────────────

export function useCanManageUsers() {
  const { hasPermission } = usePermissions()
  return hasPermission('UserManagement')
}

export function useCanAccessCapacity() {
  const { hasPermission } = usePermissions()
  return hasPermission('CapacityApplication')
}

export function useCanAccessAttendance() {
  const { hasPermission } = usePermissions()
  return hasPermission('AttendanceTrackingApplication')
}

export function useCanExportAttendanceReport() {
  const { hasPermission } = usePermissions()
  return hasPermission('AttendanceTrackingBulkReportExport')
}

export function useCanBulkActionAttendance() {
  const { hasPermission } = usePermissions()
  return hasPermission('AttendanceTrackingBulkAction')
}

export function useCanManageStaffBadging() {
  const { hasPermission } = usePermissions()
  return hasPermission('DUStaffBadging')
}
